import React, {useEffect} from "react";
import {useTranslation} from "@unibuddy/intl";
import {Button, View} from "@unibuddy/patron";
import ReactMarkdown from "react-markdown";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {useLocation} from "react-router-dom";

import ThemedLink from "shared/Navigation/components/ThemedLink/ThemedLink";
import {SSO_ENABLED} from "shared/constants";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import {IntlNamespaces} from "../../../../../translations/types";
import {INTL_SIGN_UP_WITH_EMAIL} from "../../../../..//translations/chat";
import {UnibuddyPolicyText} from "../../../RegisterNew/components/UnibuddyPolicyText";
import {GoogleAuthButton} from "../../../RegisterNew/components/GoogleAuthButton";
import {INTL_SIGN_UP_GOOGLE, INTL_SIGN_UP_GOOGLE_ERROR} from "../../../../../translations/signup";
import {getSSOErrorUrl, setSSOCtaUrl} from "../../../SSO/utils";
import {triggerSSOPopup} from "../../../SSO/SSOTrigger";
import {AuthFormError} from "../../../../../components/AuthFormError/AuthFormError";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {SSO_NEXT_PATH} from "../../../SSO/constants";
import {CreateAccountPopupHeading} from "./CreateAccountPopupHeading";

type CreateAccountPopupProps = {
    ambassadorFirstName: string;
    onClick: () => void;
    customPreSignupText?: string;
};

export const CreateAccountPopup = ({
    customPreSignupText,
    ambassadorFirstName,
    onClick,
}: CreateAccountPopupProps) => {
    const {t} = useTranslation(IntlNamespaces.CHAT);
    const isSSOEnabled = useBooleanFeatureFlag(SSO_ENABLED.key, SSO_ENABLED.value);
    const {trackEvent} = useAnalytics();
    const {slug: universitySlug} = useUniversity();
    const location = useLocation();
    const hasSSOError = location.pathname === getSSOErrorUrl();

    useEffect(() => {
        if (isSSOEnabled) {
            trackEvent(sharedEvents.ssoButtonVisible.name, {
                universitySlug,
                page: pageNames.dummyChat,
            });
        }
    }, [isSSOEnabled, trackEvent, universitySlug]);

    return (
        <View
            bgColor="white"
            justifyContent="center"
            alignItems="center"
            padding="medium-large"
            paddingBottom="small-medium"
            borderTopWidth={1}
            borderColor="grey150"
        >
            <View gap="medium-large" justifyContent="center">
                <CreateAccountPopupHeading ambassadorFirstName={ambassadorFirstName} />
                <View width="100%" alignItems="center">
                    <View
                        alignItems="center"
                        justifyContent="center"
                        gap="xsmall"
                        maxW="300px"
                        w="100%"
                    >
                        {isSSOEnabled && (
                            <GoogleAuthButton
                                variant="googleDark"
                                buttonText={t(
                                    `${IntlNamespaces.SIGNUP}:${INTL_SIGN_UP_GOOGLE.key}`,
                                    INTL_SIGN_UP_GOOGLE.defaultValue,
                                )}
                                onClick={() => {
                                    sessionStorage.setItem(
                                        `${SSO_NEXT_PATH}_${universitySlug}`,
                                        location.pathname.replace("/pre/", "/start/") ?? "",
                                    );
                                    trackEvent(sharedEvents.ssoButtonClicked.name, {
                                        universitySlug,
                                        cta: INTL_SIGN_UP_GOOGLE.defaultValue,
                                        page: pageNames.dummyChat,
                                    });
                                    setSSOCtaUrl(location.pathname);
                                    triggerSSOPopup(universitySlug);
                                }}
                            />
                        )}
                        {isSSOEnabled && hasSSOError && (
                            <AuthFormError>
                                {t(
                                    `${IntlNamespaces.SIGNUP}:${INTL_SIGN_UP_GOOGLE_ERROR.key}`,
                                    INTL_SIGN_UP_GOOGLE_ERROR.defaultValue,
                                )}
                            </AuthFormError>
                        )}
                        <Button
                            id="register"
                            block
                            color={isSSOEnabled ? "light" : "university"}
                            onClick={() => {
                                trackEvent(sharedEvents.dummyChatEmailButtonClicked.name, {
                                    universitySlug,
                                    cta: INTL_SIGN_UP_WITH_EMAIL.defaultValue,
                                    page: pageNames.dummyChat,
                                });
                                onClick();
                            }}
                        >
                            {t(INTL_SIGN_UP_WITH_EMAIL.key, INTL_SIGN_UP_WITH_EMAIL.defaultValue)}
                        </Button>
                    </View>
                </View>

                {customPreSignupText !== "" ? (
                    <ReactMarkdown
                        source={customPreSignupText}
                        renderers={{
                            link: ({href, children, nodeKey}) => (
                                <ThemedLink key={nodeKey} external to={href} target="_blank">
                                    {children}
                                </ThemedLink>
                            ),
                        }}
                    />
                ) : null}
                {isSSOEnabled && <UnibuddyPolicyText />}
            </View>
        </View>
    );
};
